import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSocialTrading } from './SocialTradingContext';

// Tournament Context - uses SocialTrading backend
const TournamentContext = createContext();

export const useTournament = () => {
  const context = useContext(TournamentContext);
  if (!context) {
    throw new Error('useTournament must be used within a TournamentProvider');
  }
  return context;
};

export const TournamentProvider = ({ children, canisterId, userId, userDisplayName }) => {
  const { 
    activeCompetitions, 
    userCompetitions, 
    userProfile, 
    userPortfolios, 
    userTrades,
    registerForCompetition, 
    createUserProfile, 
    recordDemoTrade,
    getCompetitionLeaderboard,
    loading 
  } = useSocialTrading();

  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [hasManuallyExited, setHasManuallyExited] = useState(false);

  // Get user's current competition portfolio
  const getUserPortfolio = (competitionId) => {
    return userPortfolios[competitionId] || null;
  };

  // Get user's current competition
  const getCurrentCompetition = () => {
    if (userCompetitions.length === 0) return null;
    return userCompetitions[0]; // Return first active competition
  };

  // Join a competition
  const joinCompetition = async (competitionId) => {
    if (!userDisplayName) {
      return { success: false, error: 'Please set your display name first' };
    }

    // Create user profile if doesn't exist
    if (!userProfile) {
      const profileResult = await createUserProfile(userDisplayName);
      if (!profileResult.success) {
        return profileResult;
      }
    }

    // Register for competition
    const result = await registerForCompetition(competitionId, userDisplayName);
    if (result.success) {
      setSelectedCompetition(competitionId);
    }
    return result;
  };

  // Record a trade for the current competition
  const recordTrade = async (tradeData) => {
    if (!selectedCompetition) {
      return { success: false, error: 'No active competition' };
    }

    return await recordDemoTrade(selectedCompetition, tradeData);
  };

  // Exit tournament manually
  const exitTournament = () => {
    setSelectedCompetition(null);
    // Don't set hasManuallyExited to true - allow re-selection
  };

  // Load leaderboard for a competition
  const loadLeaderboard = async (competitionId) => {
    try {
      const leaderboardData = await getCompetitionLeaderboard(competitionId);
      setLeaderboard(leaderboardData);
      
      // Find user's rank
      const userEntry = leaderboardData.find(entry => entry.user_id === userId);
      if (userEntry) {
        setUserRank(userEntry);
      }
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    }
  };

  // Auto-select competition when user joins one
  useEffect(() => {
    if (userCompetitions.length > 0 && !selectedCompetition) {
      setSelectedCompetition(userCompetitions[0][0].competition_id);
    }
  }, [userCompetitions, selectedCompetition]);

  // Auto-load leaderboard when competition changes
  useEffect(() => {
    if (selectedCompetition) {
      loadLeaderboard(selectedCompetition);
    }
  }, [selectedCompetition]);

  // Real-time updates for tournament status
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedCompetition) {
        loadLeaderboard(selectedCompetition);
      }
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [selectedCompetition]);

  // Auto-select first active competition if none selected
  useEffect(() => {
    if (activeCompetitions.length > 0 && !selectedCompetition && userCompetitions.length === 0) {
      // Only auto-select if user has actually joined competitions
      // This prevents tournament box from showing for new users
      // setSelectedCompetition(activeCompetitions[0].competition_id);
    }
  }, [activeCompetitions, userCompetitions]);

  const value = {
    // State
    activeCompetitions,
    userCompetitions,
    selectedCompetition,
    leaderboard,
    userRank,
    userProfile,
    userPortfolios,
    userTrades,
    loading,
    
    // Computed
    getUserPortfolio,
    getCurrentCompetition,
    
    // Actions
    joinCompetition,
    recordTrade,
    loadLeaderboard,
    setSelectedCompetition,
    exitTournament,
  };

  return (
    <TournamentContext.Provider value={value}>
      {children}
    </TournamentContext.Provider>
  );
}; 